import type { Category, Item, StaffMenu } from "@luckymenu/core";
import type { DraftLine } from "../ordering/draft";
import { itemMatches, normaliseSearch } from "./view";

/**
 * The item picker (ux-design.md, Item picker): a grid of every item the menu
 * has, category chips, and a search that puts short codes first, so typing
 * "PT" and Enter adds Paneer Tikka. Pure, shared by both staff apps.
 */

export type PickerChip = { category: Category; count: number };

/** Categories that have items, in menu order, hidden ones included (staff may order them). */
export function pickerChips(menu: StaffMenu): PickerChip[] {
	const counts = new Map<string, number>();
	for (const item of menu.items)
		counts.set(item.categoryId, (counts.get(item.categoryId) ?? 0) + 1);
	return menu.categories
		.filter((c) => (counts.get(c.id) ?? 0) > 0)
		.map((category) => ({ category, count: counts.get(category.id) ?? 0 }));
}

/**
 * How well an item matches: 0 its short code exactly, 1 the start of its
 * short code, 2 the start of its name, 3 any other match. Null: no match.
 */
export function matchRank(item: Item, query: string): number | null {
	const q = normaliseSearch(query);
	if (q === "") return 3;
	const code = item.shortCode?.toLowerCase() ?? "";
	const compact = q.replace(/ /g, "");
	if (code && code === compact) return 0;
	if (code?.startsWith(compact)) return 1;
	if (normaliseSearch(item.name).startsWith(q)) return 2;
	return itemMatches(item, query) ? 3 : null;
}

/**
 * The grid: with a search, matches across every category, best first
 * (menu order among equals); without, the chosen category's items, or all.
 */
export function pickerItems(
	menu: StaffMenu,
	options: { query?: string; categoryId?: string | null } = {},
): Item[] {
	const query = options.query ?? "";
	if (normaliseSearch(query) !== "") {
		return menu.items
			.map((item, index) => ({ item, index, rank: matchRank(item, query) }))
			.filter((r): r is { item: Item; index: number; rank: number } => r.rank !== null)
			.sort((a, b) => a.rank - b.rank || a.index - b.index)
			.map((r) => r.item);
	}
	const known = new Set(menu.categories.map((c) => c.id));
	// Items keep menu order: by category order, then their own.
	const order = new Map(menu.categories.map((c, i) => [c.id, i]));
	return menu.items
		.filter((i) => known.has(i.categoryId))
		.filter((i) => !options.categoryId || i.categoryId === options.categoryId)
		.map((item, index) => ({ item, index }))
		.sort(
			(a, b) =>
				(order.get(a.item.categoryId) ?? 0) - (order.get(b.item.categoryId) ?? 0) ||
				a.index - b.index,
		)
		.map((r) => r.item);
}

/** Units of each item already picked in the draft, for the count on its tile. */
export function pickedCounts(lines: readonly DraftLine[]): Map<string, number> {
	const counts = new Map<string, number>();
	for (const l of lines) counts.set(l.itemId, (counts.get(l.itemId) ?? 0) + l.quantity);
	return counts;
}

/** Moves the highlight by `delta`, staying inside the grid. -1 when the grid is empty. */
export function moveHighlight(index: number, delta: number, length: number): number {
	if (length === 0) return -1;
	return Math.max(0, Math.min(length - 1, (index < 0 ? 0 : index) + delta));
}
